import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useRouterState } from '@tanstack/react-router';
import { Landmark, LogOut, Users, Whistle, type LucideIcon } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import type {
  NotificationDto,
  OrganizationResponse,
  OrganizationSettingsResponse,
  VapidPublicKeyResponse,
} from '@bm/contracts';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import {
  getExistingPushSubscription,
  isIosSafariNonStandalone,
  isPushSupported,
  subscribeToPush,
  unsubscribeFromPush,
} from '../lib/push';
import {
  BUILTIN_BOXED_LAYOUT_ENABLED,
  BUILTIN_BOXED_WIDTH,
  BUILTIN_COLOR_DARK,
  BUILTIN_GRADIENT_ANGLE,
  BUILTIN_GRADIENT_TO_DARK,
  BUILTIN_HEADER_BORDER_COLOR_DARK,
  BUILTIN_HEADER_BORDER_COLOR_LIGHT,
  BUILTIN_HEADER_BORDER_ENABLED,
  BUILTIN_HEADER_HEIGHT,
} from '../lib/design-defaults';
import { DEFAULT_NAV_ICONS, resolveIcon } from '../lib/nav-icons';
import { useTheme, type ThemeMode } from '../lib/theme';
import { ImpersonationBanner } from './ImpersonationBanner';
import { LegalLinks } from './LegalLinks';
import { ORG_TABS } from './OrgTabs';
import { PLATFORM_TABS } from './PlatformTabs';
import { ProfileMenu } from './ProfileMenu';
import { PullToRefresh } from './PullToRefresh';

interface NavGrandchild {
  to: string;
  label: string;
  /** ORG_TABS/PLATFORM_TABS already carry one per entry (used on the pages'
   * own tab bars) — reused here so every leaf in the desktop sidebar gets
   * the same icon as its page, instead of a plain text list. */
  icon?: LucideIcon;
}

interface NavChild {
  to: string;
  label: string;
  children?: NavGrandchild[];
  /** "Verein"/"Plattform" group-header icon — used both by the mobile
   * bottom-nav "Verwaltung" speed-dial and the desktop sidebar's group
   * headings (below). */
  icon?: LucideIcon;
}

interface NavItem {
  key: string;
  to: string;
  label: string;
  show: boolean;
  children?: NavChild[];
}

/** Fußzeile im Benachrichtigungs-Dropdown: Push auf diesem Gerät an-/
 * ausschalten (§ Nutzeranfrage „Push-Benachrichtigungen … professionell
 * umsetzen"). Push ist nur ein zweiter Zustellweg für dieselben
 * Benachrichtigungen (`NotificationsService.notifyMembers` löst beides aus),
 * daher hier verankert statt als eigener Menüpunkt — siehe
 * docs/notifications.md. */
function PushToggleRow() {
  const supported = isPushSupported();
  const [error, setError] = useState<string | null>(null);

  const { data: vapid } = useQuery({
    queryKey: ['push', 'vapid-public-key'],
    queryFn: () => api<VapidPublicKeyResponse>('/push/vapid-public-key', { orgScoped: true }),
    enabled: supported,
    staleTime: Infinity,
  });
  const { data: subscription, refetch: refetchSubscription } = useQuery({
    queryKey: ['push', 'subscription'],
    queryFn: getExistingPushSubscription,
    enabled: supported,
  });

  const subscribeMut = useMutation({
    mutationFn: async () => {
      if (!vapid?.publicKey) throw new Error('not_configured');
      const sub = await subscribeToPush(vapid.publicKey);
      const json = sub.toJSON();
      await api('/push/subscribe', {
        method: 'POST',
        orgScoped: true,
        body: { endpoint: json.endpoint, keys: json.keys },
      });
    },
    onSuccess: () => {
      setError(null);
      void refetchSubscription();
    },
    onError: (err: unknown) => {
      setError(
        err instanceof Error && err.message === 'permission_denied'
          ? 'Berechtigung für Benachrichtigungen wurde abgelehnt.'
          : 'Aktivieren fehlgeschlagen — bitte erneut versuchen.',
      );
    },
  });

  const unsubscribeMut = useMutation({
    mutationFn: async () => {
      if (!subscription) return;
      const endpoint = subscription.endpoint;
      await unsubscribeFromPush(subscription);
      await api('/push/subscribe', { method: 'DELETE', orgScoped: true, body: { endpoint } });
    },
    onSuccess: () => {
      setError(null);
      void refetchSubscription();
    },
  });

  if (!supported) return null;

  // VAPID noch nicht konfiguriert (Coolify-Umgebungsvariable fehlt) — lieber
  // gar nichts anzeigen als einen Button, der nur fehlschlagen kann.
  if (vapid && !vapid.publicKey) return null;

  if (isIosSafariNonStandalone()) {
    return (
      <p className="border-t border-slate-100 px-1 pt-2 text-[11px] leading-snug text-slate-400">
        Für Push-Benachrichtigungen diese Seite über „Zum Home-Bildschirm hinzufügen" als App
        installieren.
      </p>
    );
  }

  const pending = subscribeMut.isPending || unsubscribeMut.isPending;

  return (
    <div className="border-t border-slate-100 pt-2">
      <div className="flex items-center justify-between px-1">
        <span className="text-xs text-slate-500">
          Push-Benachrichtigungen {subscription ? 'aktiv' : 'auf diesem Gerät'}
        </span>
        <button
          disabled={pending}
          onClick={() => (subscription ? unsubscribeMut.mutate() : subscribeMut.mutate())}
          className="rounded-md px-2 py-1 text-xs font-medium text-brand-accent hover:bg-brand-accent/10 disabled:opacity-50"
        >
          {subscription ? 'Deaktivieren' : 'Aktivieren'}
        </button>
      </div>
      {error && <p className="px-1 pt-1 text-[11px] text-red-500">{error}</p>}
    </div>
  );
}

function NotificationsBell() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const { data } = useQuery({
    queryKey: ['notifications'],
    queryFn: () =>
      api<{ data: NotificationDto[]; unread: number }>('/notifications?pageSize=15', { orgScoped: true }),
    refetchInterval: 60_000,
  });
  const markRead = useMutation({
    mutationFn: () => api('/notifications/read', { method: 'PATCH', orgScoped: true, body: { ids: 'all' } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative rounded-md px-2 py-1 text-slate-500 hover:bg-slate-100"
        aria-label="Benachrichtigungen"
      >
        🔔
        {!!data?.unread && (
          <span className="absolute -right-1 -top-1 rounded-full bg-red-500 px-1 text-[10px] text-white">
            {data.unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 z-10 mt-1 w-80 rounded-md border border-slate-200 bg-white p-2 shadow-lg">
          <div className="flex items-center justify-between px-1 pb-1 text-xs text-slate-400">
            <span>Benachrichtigungen</span>
            {!!data?.unread && (
              <button onClick={() => markRead.mutate()} className="text-brand-accent">
                alle gelesen
              </button>
            )}
          </div>
          <ul className="max-h-80 space-y-1 overflow-y-auto text-sm">
            {data?.data.map((n) => (
              <li
                key={n.id}
                className={`rounded-md px-2 py-1 ${n.readAt ? 'text-slate-500' : 'bg-blue-50'}`}
              >
                <div className="font-medium">{n.title}</div>
                {n.body && <div className="text-xs text-slate-500">{n.body}</div>}
              </li>
            ))}
            {data?.data.length === 0 && (
              <li className="px-2 py-2 text-xs text-slate-400">Keine Benachrichtigungen.</li>
            )}
          </ul>
          <PushToggleRow />
        </div>
      )}
    </div>
  );
}

/** hex (#rrggbb) + opacity (0–1, default fully opaque) -> "rgba(r, g, b, a)".
 * Lets the sidebar/content backgrounds be see-through onto the org's
 * background image instead of only ever fully covering it. */
function hexToRgba(hex: string | undefined, opacity: number | undefined): string | undefined {
  if (!hex) return undefined;
  const m = /^#([0-9a-fA-F]{2})([0-9a-fA-F]{2})([0-9a-fA-F]{2})$/.exec(hex);
  if (!m) return undefined;
  const [r, g, b] = [m[1]!, m[2]!, m[3]!].map((h) => parseInt(h, 16));
  return `rgba(${r}, ${g}, ${b}, ${opacity ?? 1})`;
}

/** A two-stop `linear-gradient(...)`, both stops sharing the same opacity —
 * only built when a "to" color is actually configured (gradient is opt-in;
 * without it the background stays a flat `background-color`, since a CSS
 * `background-color` can't itself hold a gradient). Applied as an inline
 * `backgroundImage` layered on top of the flat `--sidebar-bg`/`--content-bg`
 * custom property, which stays as the fallback/base. */
function gradientCss(
  from: string | undefined,
  to: string | undefined,
  opacity: number | undefined,
  angle: number | undefined,
): string | undefined {
  if (!to) return undefined;
  const fromRgba = hexToRgba(from, opacity);
  const toRgba = hexToRgba(to, opacity);
  if (!fromRgba || !toRgba) return undefined;
  return `linear-gradient(${angle ?? 135}deg, ${fromRgba}, ${toRgba})`;
}

/** Dark mode's built-in fallback, one level further down than the light
 * chain's: light stops at `undefined` and lets the Tailwind class's own
 * `var(--x, #builtin-light)` supply the final default (tailwind.config.js);
 * dark mode instead sets a real inline value at every level — see
 * `effColor` below — so it needs its own explicit built-in for every color
 * field, not just the ones with a "modern dark" palette in design-defaults.
 * Non-color fields (opacity, header height, boxed width, gradient angle)
 * reuse the same built-in regardless of theme — there's no reason those
 * would differ by color scheme, only the colors themselves do. */
const BUILTIN_DARK_FALLBACK: NonNullable<OrganizationResponse['colors']> = {
  ...BUILTIN_COLOR_DARK,
  ...BUILTIN_GRADIENT_TO_DARK,
  headerHeight: BUILTIN_HEADER_HEIGHT,
  boxedWidth: BUILTIN_BOXED_WIDTH,
  boxedLayoutEnabled: BUILTIN_BOXED_LAYOUT_ENABLED,
  sidebarGradientAngle: BUILTIN_GRADIENT_ANGLE,
  contentGradientAngle: BUILTIN_GRADIENT_ANGLE,
  headerBorderEnabled: BUILTIN_HEADER_BORDER_ENABLED,
  headerBorderColor: BUILTIN_HEADER_BORDER_COLOR_DARK,
};

/** One design field's effective value for the given theme: the org's own
 * override for that theme if it set one, else the platform-wide default for
 * that theme (Plattform › Design (Global)), else — light only — `undefined`
 * (the caller's/Tailwind's own hard-coded built-in look applies); dark mode
 * instead resolves all the way to `BUILTIN_DARK_FALLBACK`, since there is no
 * CSS-level dark default to fall through to (dark mode used to be a single
 * hard-coded override for everyone, see index.css git history — now it's
 * just the last link in the same per-field fallback chain light mode always
 * had). Light and dark never mix: a dark viewer never sees a light-only
 * override, and vice versa. */
function effColor<K extends keyof NonNullable<OrganizationResponse['colors']>>(
  org: OrganizationResponse | undefined,
  key: K,
  theme: ThemeMode,
): NonNullable<OrganizationResponse['colors']>[K] {
  if (theme === 'dark') {
    return org?.colorsDark?.[key] ?? org?.platformDefaultColorsDark?.[key] ?? BUILTIN_DARK_FALLBACK[key];
  }
  return org?.colors?.[key] ?? org?.platformDefaultColors?.[key];
}

type BoxedBackground = { kind: 'image'; fileId: string } | { kind: 'color'; color: string };

/** The fixed, non-scrolling "boxed layout" background is either an uploaded
 * image or a flat color — never both (an image, if present, wins). Unlike
 * `effColor`, this resolves per *level* rather than per field: an org that
 * set only a color of its own fully overrides a platform default image (and
 * vice versa), since the two options are one visual choice, not independent
 * settings. `null` = neither level set anything — no background layer at
 * all (today's built-in default). */
function resolveBoxedBackground(org: OrganizationResponse | undefined, theme: ThemeMode): BoxedBackground | null {
  // The uploaded image itself is the same for both themes (see the "no
  // per-theme file uploads" scope note on the two Design pages) — only the
  // flat-color fallback used when no image is set differs by theme.
  if (org?.backgroundImageFileId) return { kind: 'image', fileId: org.backgroundImageFileId };
  const color = theme === 'dark' ? (org?.colorsDark?.pageBackgroundColor ?? org?.platformDefaultColorsDark?.pageBackgroundColor) : org?.colors?.pageBackgroundColor;
  if (color) return { kind: 'color', color };
  if (org?.platformDefaultBackgroundImageFileId) {
    return { kind: 'image', fileId: org.platformDefaultBackgroundImageFileId };
  }
  if (theme === 'light' && org?.platformDefaultColors?.pageBackgroundColor) {
    return { kind: 'color', color: org.platformDefaultColors.pageBackgroundColor };
  }
  return null;
}

/** Mobile-only page title shown in the header next to the logo (§
 * Nutzeranfrage) — a fixed path→Titel-Tabelle statt generischer
 * Routen-Metadaten (TanStack Router trägt hier keine `meta`/`staticData`,
 * und 28 `createRoute`-Aufrufe dafür umzubauen wäre für eine reine
 * Kopfzeilen-Anzeige unverhältnismäßig). Jeder Eintrag spiegelt exakt den
 * Text, den die jeweilige Seite ohnehin schon als eigene `<h1>` zeigt (die
 * bleibt unverändert bestehen, nur auf Mobile zusätzlich versteckt, siehe
 * `hidden md:block` an jeder Seiten-`<h1>`) — zwei bewusste Ausnahmen:
 * Dashboard zeigt hier den statischen Titel "Dashboard" statt der
 * dynamischen Begrüßung, und `/members/:id` zeigt "Mitglieder" statt des
 * geladenen Mitgliedsnamens (der lebt nur in der Seite selbst, nicht in
 * dieser rein statischen Tabelle). `null` (Login/Invite/Impressum/…) zeigt
 * gar nichts — diese Seiten laufen ohnehin außerhalb von `AppShell`. */
function pageTitleForPath(path: string): string | null {
  if (path === '/') return 'Dashboard';
  if (path === '/training' || path.startsWith('/training/events/')) return 'Training';
  if (path === '/season') return 'Saisonspiele';
  if (path === '/calendar') return 'Kalender';
  if (path === '/equipment') return 'Equipment';
  if (path === '/trainer') return 'Trainer';
  if (path === '/members' || path.startsWith('/members/')) return 'Mitglieder';
  if (path === '/organization/mail') return 'SMTP Server';
  if (path.startsWith('/organization')) return 'Verein';
  if (path.startsWith('/platform')) {
    return PLATFORM_TABS.find((t) => t.to === path)?.label ?? 'Plattform';
  }
  return null;
}

/** Applies the active org's brand colors as CSS custom properties on <html>,
 * so `bg-brand-accent` / `bg-header-bg` / `bg-sidebar-bg` / `bg-content-bg`
 * (tailwind.config.js) reflect the club's own Design settings at runtime —
 * one build, many orgs. */
function useOrgTheme(hasOrg: boolean) {
  const { theme } = useTheme();
  const { data: org } = useQuery({
    queryKey: ['org'],
    queryFn: () => api<OrganizationResponse>('/org', { orgScoped: true }),
    enabled: hasOrg,
  });

  useEffect(() => {
    const root = document.documentElement;
    const vars: Record<string, string | undefined> = {
      '--brand-accent': effColor(org, 'primary', theme),
      '--header-bg': effColor(org, 'headerBackground', theme),
      '--header-text': effColor(org, 'headerText', theme),
      '--sidebar-bg': hexToRgba(effColor(org, 'sidebarBackground', theme), effColor(org, 'sidebarOpacity', theme)),
      '--content-bg': hexToRgba(effColor(org, 'contentBackground', theme), effColor(org, 'contentOpacity', theme)),
    };
    for (const [k, v] of Object.entries(vars)) {
      if (v) root.style.setProperty(k, v);
      else root.style.removeProperty(k);
    }
  }, [org, theme]);

  return org;
}

export function AppShell({ children }: { children: ReactNode }) {
  const { me, logout, can } = useAuth();
  const path = useRouterState({ select: (s) => s.location.pathname });
  const pageTitle = pageTitleForPath(path);
  // "Verwaltung" in the mobile bottom nav: with only one of Verein/Plattform
  // available it's a plain link (below), but a user who is both a club admin
  // and a platform admin needs a way to reach both — a small speed-dial that
  // pops the two choices up above the tab on tap, see the bottom nav render.
  const [adminMenuOpen, setAdminMenuOpen] = useState(false);
  useEffect(() => setAdminMenuOpen(false), [path]);

  const hasOrg = !!me?.activeOrganizationId;
  const { theme } = useTheme();
  const org = useOrgTheme(hasOrg);
  const sidebarGradient = gradientCss(
    effColor(org, 'sidebarBackground', theme),
    effColor(org, 'sidebarBackgroundTo', theme),
    effColor(org, 'sidebarOpacity', theme),
    effColor(org, 'sidebarGradientAngle', theme),
  );
  const contentGradient = gradientCss(
    effColor(org, 'contentBackground', theme),
    effColor(org, 'contentBackgroundTo', theme),
    effColor(org, 'contentOpacity', theme),
    effColor(org, 'contentGradientAngle', theme),
  );
  const { data: settings } = useQuery({
    queryKey: ['org', 'settings'],
    queryFn: () => api<OrganizationSettingsResponse>('/org/settings', { orgScoped: true }),
    enabled: hasOrg,
  });
  const navIcons = settings?.navIcons ?? {};
  const perms = new Set(me?.permissions ?? []);
  // "Verwaltung" bündelt Verein/Plattform als Unterpunkte statt zweier eigener
  // Top-Level-Einträge — ein normales Mitglied, das auf keine der beiden Seiten darf,
  // sieht den Punkt dann konsequent gar nicht (show: adminChildren.length > 0).
  const adminChildren: NavChild[] = [];
  if (hasOrg && perms.has('organization.edit')) {
    adminChildren.push({
      to: '/organization',
      label: 'Verein',
      icon: Users,
      children: ORG_TABS.filter((t) => !t.perm || can(t.perm)),
    });
  }
  if (me?.user.isPlatformAdmin) {
    adminChildren.push({ to: '/platform/organizations', label: 'Plattform', icon: Landmark, children: PLATFORM_TABS });
  }
  // Additives Muster: die vereinsweite Permission ODER das Trainer-Kennzeichen
  // genügt, unabhängig von `organization.edit` — siehe docs/trainers.md.
  if (hasOrg && (perms.has('trainers.record') || perms.has('trainers.manage') || !!me?.user.isTrainer)) {
    adminChildren.push({ to: '/trainer', label: 'Trainer', icon: Whistle });
  }
  const nav: NavItem[] = [
    { key: 'dashboard', to: '/', label: 'Dashboard', show: true },
    { key: 'training', to: '/training', label: 'Training', show: hasOrg && perms.has('training.view') },
    { key: 'season', to: '/season', label: 'Saison', show: hasOrg && perms.has('season.view') },
    { key: 'calendar', to: '/calendar', label: 'Kalender', show: hasOrg && perms.has('calendar.view') },
    { key: 'equipment', to: '/equipment', label: 'Equipment', show: hasOrg && perms.has('equipment.view') },
    {
      key: 'administration',
      to: adminChildren[0]?.to ?? '/organization',
      label: 'Verwaltung',
      show: adminChildren.length > 0,
      children: adminChildren,
    },
  ].filter((n) => n.show);
  const isActive = (to: string) => path === to || (to !== '/' && path.startsWith(`${to}/`));
  const basePath = (p: string) => `/${p.split('/')[1] ?? ''}`;
  // A section with (grand-)children counts as active anywhere under any descendant's
  // base path, not just its own index page — otherwise the nested sub-links would
  // never appear while browsing one of them.
  const descendantPaths = (n: NavItem): string[] => [
    n.to,
    ...(n.children ?? []).flatMap((c) => [c.to, ...(c.children ?? []).map((g) => g.to)]),
  ];
  const isSectionActive = (n: NavItem): boolean =>
    n.children && n.children.length > 0
      ? descendantPaths(n).some((t) => path === t || path.startsWith(`${basePath(t)}/`))
      : isActive(n.to);
  const isChildActive = (c: NavChild): boolean =>
    c.children && c.children.length > 0
      ? [c.to, ...c.children.map((g) => g.to)].some((t) => path === t || path.startsWith(`${basePath(t)}/`))
      : isActive(c.to);
  const iconFor = (key: string) => resolveIcon(navIcons[key] ?? DEFAULT_NAV_ICONS[key]);
  // Logo grows/shrinks with the configured header height (py-2 = 16px of
  // vertical padding to leave room for) instead of a fixed 32px, so a taller
  // header doesn't leave the logo looking stranded and tiny.
  const headerHeight = effColor(org, 'headerHeight', theme);
  const logoSize = headerHeight ? Math.max(24, Math.min(headerHeight - 16, 128)) : 32;
  const headerBorderEnabled = effColor(org, 'headerBorderEnabled', theme) ?? BUILTIN_HEADER_BORDER_ENABLED;
  const headerBorderColor =
    effColor(org, 'headerBorderColor', theme) ??
    (theme === 'dark' ? BUILTIN_HEADER_BORDER_COLOR_DARK : BUILTIN_HEADER_BORDER_COLOR_LIGHT);
  // Same fallback chain as the colors above: the org's own upload, else the
  // platform-wide default (Plattform › Design (Global)), else nothing.
  const effectiveLogoFileId = org?.logoFileId ?? org?.platformDefaultLogoFileId ?? null;
  const boxedLayoutEnabled = effColor(org, 'boxedLayoutEnabled', theme) ?? BUILTIN_BOXED_LAYOUT_ENABLED;
  const boxedBackground = boxedLayoutEnabled ? resolveBoxedBackground(org, theme) : null;
  const boxedWidth = effColor(org, 'boxedWidth', theme);
  const boxedWidthStyle = boxedLayoutEnabled ? (boxedWidth ? { maxWidth: `${boxedWidth}px` } : undefined) : { maxWidth: 'none' };

  return (
    <>
      {boxedBackground && (
        // A dedicated `position: fixed` layer instead of `background-attachment:
        // fixed` on the scrolling container — the latter is notoriously
        // unreliable on mobile Safari (can repaint late or leave a blank gap
        // right under the header while the page settles), while a plain fixed
        // element is universally solid. `-z-10` keeps it behind everything;
        // `aria-hidden` because it's purely decorative. Below the boxed
        // width, the content itself already fills 100% of the screen
        // (`max-w-6xl` + `mx-auto` only actually constrains once the
        // viewport exceeds it), so this layer is inert there by design —
        // `hidden lg:block` makes that explicit instead of relying only on
        // the content covering it, as a second line of defense against a
        // page-level horizontal-overflow bug on mobile momentarily exposing
        // it (§ Nutzerfeedback "Inhalt wird außerhalb des Content-Bereichs
        // dargestellt" — see also the `overflow-x: hidden` in index.css).
        <div
          aria-hidden="true"
          className="fixed inset-0 -z-10 hidden bg-cover bg-center lg:block"
          style={
            boxedBackground.kind === 'image'
              ? { backgroundImage: `url(/api/org/files/${boxedBackground.fileId})` }
              : { backgroundColor: boxedBackground.color }
          }
        />
      )}
      <PullToRefresh />
      {/* `overflow-x-hidden` sitzt bewusst hier (auf einem `min-h-full`-,
          nicht `h-full`-Element) statt auf `html`/`body` in index.css —
          siehe die ausführliche Begründung dort. Fängt weiterhin das
          gleiche Symptom ab (eine Seite mit fehlendem `flex-wrap` in ihrer
          Kopfzeile lässt sonst die ganze App horizontal überlaufen, wodurch
          der fixierte Hintergrund oben sichtbar wird), ohne `body` in eine
          eigene, von `window` abgekoppelte Scroll-Box zu verwandeln. */}
      {/* Mobile (§ Nutzeranfrage „mehr die Breite … ausnutzen … moderner und
       * professioneller"): kein Seitenabstand/Zwischenraum mehr unter `md:` —
       * Header und Inhaltsbereich sitzen auf dem Handy randlos/flächenbündig
       * statt als schwebende Karten mit Lücke dazwischen (die „Glas-
       * Sidebar"-Optik bleibt ab `md:` unverändert bestehen, dort macht der
       * sichtbare Zwischenraum vor einem Hintergrundbild auch tatsächlich
       * einen Unterschied — auf Mobile war der ohnehin nie sichtbar, siehe
       * `boxedBackground`s `hidden lg:block` unten). */}
      <div className="flex min-h-full flex-col gap-0 overflow-x-hidden px-0 pb-0 pt-0 md:gap-4 md:px-4 md:pb-4 md:pt-4">
        <ImpersonationBanner />
        <header
          className={`relative mx-auto w-full max-w-6xl flex-shrink-0 bg-header-bg text-header-text md:rounded-3xl md:shadow-sm ${
            headerBorderEnabled ? 'border-b md:border' : ''
          }`}
          style={{
            ...boxedWidthStyle,
            ...(headerHeight ? { minHeight: `${headerHeight}px` } : undefined),
            ...(headerBorderEnabled ? { borderColor: headerBorderColor } : undefined),
          }}
        >
          <div className="flex h-full w-full items-center gap-3 px-4 py-2">
            {/* Mobile: logo on the left; notifications + profile icon on the
             * right. § Nutzeranfrage „Hamburgermenü soll weg" — Organisation
             * wechseln und Abmelden leben seither im Profilmenü
             * (`ProfileMenu.tsx`) statt eines eigenen, separaten
             * Dropdown-Panels hier. */}
            <div className="flex min-w-0 items-center gap-2.5 md:hidden">
              {effectiveLogoFileId ? (
                <img
                  src={`/api/org/files/${effectiveLogoFileId}`}
                  alt={org?.name ?? 'Logo'}
                  className="shrink-0 rounded object-contain"
                  style={{ width: logoSize, height: logoSize }}
                />
              ) : (
                // Ohne eigenes Logo (Fallback-Text, seltener Fall) darf dieser
                // Text bei wenig Platz mitschrumpfen statt dem Seitentitel
                // daneben die komplette Enge aufzuzwingen — sonst bliebe vom
                // Titel auf sehr schmalen Geräten nur noch ein Buchstabe übrig.
                <span className="min-w-0 truncate font-semibold">Badminton Manager</span>
              )}
              {/* § Nutzeranfrage „die Überschrift einer Seite soll oben im
                  Header rechts neben dem Logo stehen, auf dem Handy": ersetzt
                  nicht die eigene `<h1>` jeder Seite (die bleibt auf Desktop
                  unverändert sichtbar, siehe die `hidden md:block`-Klasse
                  dort) — nur eine zusätzliche, rein mobile Kopie hier oben,
                  aus `pageTitleForPath()` unten (fester Pfad→Titel-Mapping,
                  spiegelt exakt den Text, den die jeweilige Seite als `<h1>`
                  ohnehin schon zeigt). */}
              {pageTitle && (
                <span className="min-w-0 truncate text-lg font-semibold text-header-text">{pageTitle}</span>
              )}
            </div>
            <div className="ml-auto flex items-center gap-1 md:hidden">
              {hasOrg && perms.has('notifications.view') && <NotificationsBell />}
              <ProfileMenu />
            </div>

            {/* Desktop: full header, unchanged. */}
            <div className="hidden items-center gap-2.5 md:flex">
              {effectiveLogoFileId ? (
                <img
                  src={`/api/org/files/${effectiveLogoFileId}`}
                  alt=""
                  className="shrink-0 rounded object-contain"
                  style={{ width: logoSize, height: logoSize }}
                />
              ) : null}
              <div className="leading-tight">
                <div className="font-semibold">Badminton Manager</div>
                {org?.name && <div className="text-xs opacity-70">{org.name}</div>}
              </div>
            </div>

            <div className="ml-auto hidden items-center gap-2 md:flex">
              {hasOrg && perms.has('notifications.view') && <NotificationsBell />}
              <ProfileMenu />
            </div>
          </div>
        </header>

        <div className="mx-auto flex w-full max-w-6xl flex-1 flex-col gap-0 md:flex-row md:gap-4" style={boxedWidthStyle}>
          <aside
            className="hidden w-64 shrink-0 flex-col rounded-3xl border border-slate-200 bg-gradient-to-b from-brand-accent/[0.06] to-transparent bg-sidebar-bg p-4 shadow-sm md:flex"
            style={sidebarGradient ? { backgroundImage: sidebarGradient } : undefined}
          >
            <nav className="space-y-1">
              {nav.map((n) => {
                const Icon = iconFor(n.key);
                const sectionActive = isSectionActive(n);
                return (
                  <div key={n.to}>
                    <Link
                      to={n.to}
                      className={`relative flex items-center gap-2.5 rounded-2xl py-2 text-sm transition-colors ${
                        sectionActive
                          ? 'bg-brand-accent/10 pl-4 pr-3 font-semibold text-brand-accent'
                          : 'px-3 text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      {sectionActive && (
                        <span className="absolute left-1.5 top-1/2 h-[18px] w-1 -translate-y-1/2 rounded-full bg-brand-accent" />
                      )}
                      {sectionActive ? (
                        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-brand-accent text-white">
                          <Icon size={14} />
                        </span>
                      ) : (
                        <Icon size={16} className="shrink-0" />
                      )}
                      {n.label}
                    </Link>
                    {n.children && n.children.length > 0 && sectionActive && (
                      <div className="ml-1 mt-3 space-y-3 border-t border-slate-200/70 pt-3">
                        {n.children.map((c) => {
                          const childActive = isChildActive(c);
                          const GroupIcon = c.icon;
                          return (
                            <div key={c.to}>
                              {/* Gruppen-Überschrift ("Verein"/"Plattform") — bewusst kein
                                  Listenpunkt auf derselben Ebene wie die Unterseiten darunter
                                  (kleiner, dezenter, in Versalien mit eigenem Icon), sonst
                                  wirkt sie wie eine weitere Unterseite statt wie eine
                                  Kategorie. Bleibt anklickbar (führt zur ersten Unterseite
                                  der Gruppe). */}
                              <Link
                                to={c.to}
                                className={`flex items-center gap-1.5 rounded-lg px-2 py-1 text-[11px] font-bold uppercase tracking-wider transition-colors ${
                                  childActive ? 'text-brand-accent' : 'text-slate-400 hover:text-slate-600'
                                }`}
                              >
                                {GroupIcon && <GroupIcon size={11} className="shrink-0" />}
                                {c.label}
                              </Link>
                              {c.children && c.children.length > 0 && childActive && (
                                <div className="ml-2 mt-1 space-y-0.5 border-l border-slate-200 pl-3">
                                  {c.children.map((g) => {
                                    const GrandIcon = g.icon;
                                    const grandActive = path === g.to;
                                    return (
                                      <Link
                                        key={g.to}
                                        to={g.to}
                                        className={`flex items-center gap-2 rounded-xl px-2 py-1.5 text-sm transition-colors ${
                                          grandActive
                                            ? 'bg-brand-accent/10 font-medium text-brand-accent'
                                            : 'text-slate-500 hover:bg-slate-50 hover:text-slate-700'
                                        }`}
                                      >
                                        {GrandIcon && <GrandIcon size={14} className="shrink-0" />}
                                        {g.label}
                                      </Link>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </nav>
            {me && (
              <div className="mt-auto flex items-center gap-2.5 rounded-2xl border border-slate-200/70 bg-slate-50 p-2.5">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-accent/15 text-xs font-bold text-brand-accent">
                  {(me.user.firstName ?? me.user.email).charAt(0).toUpperCase()}
                </span>
                <div className="min-w-0 flex-1 leading-tight">
                  <div className="truncate text-[13px] font-semibold text-slate-700">
                    {me.user.firstName ?? me.user.email}
                  </div>
                  <div className="truncate text-[11px] text-slate-400">
                    {me.memberships.find((m) => m.organizationId === me.activeOrganizationId)?.roleName ?? '—'}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => logout()}
                  title="Abmelden"
                  aria-label="Abmelden"
                  className="shrink-0 rounded-full p-1.5 text-slate-400 hover:bg-slate-200/70 hover:text-slate-600"
                >
                  <LogOut size={14} />
                </button>
              </div>
            )}
            <LegalLinks className="pt-3" />
            <a
              href={`https://github.com/StefanM1990/badminton-manager/commit/${__GIT_SHA__}`}
              target="_blank"
              rel="noreferrer"
              className="pt-2 text-center text-xs text-slate-400 hover:text-slate-600"
            >
              Build {__GIT_SHA__}
            </a>
          </aside>

          <main
            className="min-w-0 flex-1 bg-content-bg p-4 pb-[calc(5rem_+_env(safe-area-inset-bottom))] md:rounded-3xl md:border md:border-slate-200 md:pb-4 md:shadow-sm"
            style={contentGradient ? { backgroundImage: contentGradient } : undefined}
          >
            {children}
          </main>

          {/* § Nutzeranfrage „unten bündig, aber iPhone-Gestenleiste nicht
           * beeinträchtigen": die Bar selbst sitzt jetzt flächenbündig auf
           * `bottom-0` (kein schwebender Abstand zur Bildschirmkante mehr,
           * nur oben abgerundet statt rundum) — die Sicherheitszone kommt
           * stattdessen als zusätzliches `padding-bottom` auf jeden
           * Tap-Bereich (`pb-[calc(0.5rem+env(safe-area-inset-bottom))]`
           * unten), das Icon/Label bleibt dadurch wie zuvor oberhalb der
           * iPhone-Home-Indicator-Gestenzone, während der weiße Hintergrund
           * der Bar bis zur echten Bildschirmkante durchläuft statt dort eine
           * Lücke zu zeigen. Ohne diese Zone interzeptiert iOS Taps nahe der
           * Kante als eigene Swipe-up-Geste statt sie an die Buttons
           * weiterzureichen. `main`'s bottom padding oben deckt die (nun
           * minimal kleinere) Bar-Höhe weiterhin mit Reserve ab.
           * `will-change-transform` + `transform-gpu` force this onto its
           * own compositor layer — without it, mobile Chrome/Safari
           * sometimes render a stale frame mid-fling where a plain
           * `position: fixed` bar hasn't caught up with the new scroll
           * offset yet, making it appear to briefly drift up into the
           * middle of the page instead of staying pinned to the viewport
           * bottom (§ Nutzerfeedback „Das Menü bleibt unten nicht
           * fixiert"). `transform-gpu` (Tailwind's `translate3d(0,0,0)`
           * utility) is the stronger of the two hints — `will-change`
           * alone is only a hint the browser may ignore under memory
           * pressure, `transform-gpu` forces the compositor layer
           * unconditionally. This is a best-effort mitigation for a
           * long-standing WebKit quirk around `position: fixed` during the
           * dynamic toolbar's show/hide animation, not something fully
           * fixable from CSS alone — verify on a real iPhone after
           * deploy. */}
          <nav className="fixed inset-x-0 bottom-0 flex rounded-t-xl border border-slate-200 bg-white shadow-lg will-change-transform transform-gpu md:hidden">
            {nav.map((n) => {
              const Icon = iconFor(n.key);
              // "Verwaltung" with more than one of its `adminChildren`
              // available (Verein/Plattform/Trainer — e.g. a
              // platform admin who's also a club admin) can't be a single
              // link to any one of them — tap it to pop the choices up above
              // the tab instead of guessing which one to jump to. Anchored
              // `right-2` (not centered) because "administration" is always
              // the last `nav` entry, i.e. the rightmost tab — a centered
              // popup ran off the right edge of the screen there (§
              // Nutzeranfrage); re-check this anchor if that ordering ever
              // changes.
              if (n.key === 'administration' && (n.children?.length ?? 0) > 1) {
                const sectionActive = isSectionActive(n);
                return (
                  <div key={n.to} className="relative flex flex-1">
                    {adminMenuOpen && (
                      <div
                        className="fixed inset-0 z-30"
                        onClick={() => setAdminMenuOpen(false)}
                        aria-hidden="true"
                      />
                    )}
                    {adminMenuOpen && (
                      <div className="admin-speed-dial absolute bottom-full right-2 z-40 mb-3 flex w-48 flex-col gap-1 rounded-2xl border border-slate-200 bg-white p-1.5 shadow-xl">
                        {n.children!.map((c) => {
                          const ChildIcon = c.icon ?? Landmark;
                          return (
                            <Link
                              key={c.to}
                              to={c.to}
                              onClick={() => setAdminMenuOpen(false)}
                              className="flex items-center gap-2.5 rounded-xl px-2.5 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 active:bg-slate-100"
                            >
                              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-accent/10 text-brand-accent">
                                <ChildIcon size={16} />
                              </span>
                              {c.label}
                            </Link>
                          );
                        })}
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => setAdminMenuOpen((o) => !o)}
                      aria-expanded={adminMenuOpen}
                      aria-label={n.label}
                      className={`flex flex-1 flex-col items-center gap-0.5 pt-2 pb-[calc(0.5rem+env(safe-area-inset-bottom))] text-center text-xs ${
                        sectionActive || adminMenuOpen ? 'font-semibold text-brand-accent' : 'text-slate-500'
                      }`}
                    >
                      <Icon size={18} />
                      {n.label}
                    </button>
                  </div>
                );
              }
              return (
                <Link
                  key={n.to}
                  to={n.to}
                  className={`flex flex-1 flex-col items-center gap-0.5 pt-2 pb-[calc(0.5rem+env(safe-area-inset-bottom))] text-center text-xs ${
                    isActive(n.to) ? 'font-semibold text-brand-accent' : 'text-slate-500'
                  }`}
                >
                  <Icon size={18} />
                  {n.label}
                </Link>
              );
            })}
          </nav>
        </div>
      </div>
    </>
  );
}
